import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, Briefcase, DollarSign, Zap } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import AiUsagePanel from "@/components/admin/AiUsagePanel";

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [kpis, setKpis] = useState(null);
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/kpis").then((r) => setKpis(r.data)).catch(() => {});
    api.get("/admin/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const toggle = async (key) => {
    setSaving(true);
    const next = { ...config, [key]: !config[key] };
    setConfig(next);
    try { await api.patch("/admin/config", { [key]: next[key] }); }
    finally { setSaving(false); }
  };

  const kpiCards = [
    { label: t("adminX.dashboard.kpiUsers"), value: kpis?.users?.total, sub: t("adminX.dashboard.kpiUsersSub", { c: kpis?.users?.clients || 0, p: kpis?.users?.providers || 0 }), icon: Users },
    { label: t("adminX.dashboard.kpiJobs"), value: kpis?.jobs?.last_30d, sub: t("adminX.dashboard.kpiJobsSub", { n: kpis?.jobs?.flagged || 0 }), icon: Briefcase },
    { label: t("adminX.dashboard.kpiSubs"), value: kpis?.subscriptions?.active, sub: t("adminX.dashboard.kpiSubsSub", { pro: kpis?.subscriptions?.pro || 0, master: kpis?.subscriptions?.master || 0 }), icon: DollarSign },
    { label: t("adminX.dashboard.kpiAccept"), value: (kpis?.quotes?.acceptance_rate_pct ?? "—") + "%", sub: t("adminX.dashboard.kpiAcceptSub", { n: kpis?.quotes?.last_30d || 0 }), icon: Zap },
  ];

  const toggles = [
    ["ai_kill_switch_active", t("adminX.config.fields.ai_kill_switch_active"), "danger"],
    ["feature_ai_smart_description", t("adminX.config.fields.feature_ai_smart_description"), ""],
    ["feature_ai_image_diagnosis", t("adminX.config.fields.feature_ai_image_diagnosis"), ""],
    ["feature_ai_comparator", t("adminX.config.fields.feature_ai_comparator"), ""],
    ["feature_ai_estimator", t("adminX.config.fields.feature_ai_estimator"), ""],
    ["feature_ai_search_intent", t("adminX.config.fields.feature_ai_search_intent"), ""],
    ["feature_ai_suggest_categories", t("adminX.config.fields.feature_ai_suggest_categories"), ""],
  ];

  return (
    <DashboardLayout role="admin">
      <div data-testid="admin-dashboard">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">{t("adminX.dashboard.title")}</h1>
        <p className="mt-1 text-smoke">{t("adminX.dashboard.subtitle")}</p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
          {kpiCards.map((k) => {
            const Icon = k.icon;
            return (
              <div key={k.label} className="card-base p-4" data-testid={`admin-kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
                <Icon size={18} className="text-smoke" />
                <p className="mt-3 font-mono text-2xl font-bold text-worker">{k.value ?? "—"}</p>
                <p className="text-xs uppercase tracking-widest text-smoke font-display">{k.label}</p>
                <p className="text-[11px] text-smoke mt-0.5">{k.sub}</p>
              </div>
            );
          })}
        </div>

        <AiUsagePanel t={t} />

        <div className="mt-10">
          <h2 className="font-display text-2xl font-bold text-worker">{t("adminX.dashboard.platformConfig")}</h2>
          <p className="text-smoke text-sm">{t("adminX.dashboard.platformConfigSub")}</p>

          {config && (
            <div className="mt-6 card-base p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="label-base">{t("adminX.dashboard.monthlyBudget")}</p>
                  <p className="font-mono text-3xl font-bold text-worker">${config.ai_ceiling_usd}</p>
                </div>
                <div>
                  <p className="label-base">{t("adminX.dashboard.extraCreditPrice")}</p>
                  <p className="font-mono text-3xl font-bold text-worker">${config.extra_credit_price_usd}</p>
                </div>
              </div>
              <hr className="my-6 border-sand" />
              <h3 className="font-display font-bold text-worker mb-3">{t("adminX.dashboard.featureToggles")}</h3>
              <div className="grid sm:grid-cols-2 gap-2">
                {toggles.map(([key, label, variant]) => (
                  <label key={key} className={`flex items-center justify-between p-3 rounded-sm border ${variant === "danger" ? "border-danger bg-danger/5" : "border-sand bg-cream"}`} data-testid={`toggle-${key}`}>
                    <span className={`font-display text-sm font-medium ${variant === "danger" ? "text-danger" : "text-worker"}`}>{label}</span>
                    <input type="checkbox" checked={config[key] || false} onChange={() => toggle(key)} disabled={saving}
                      className="w-5 h-5 accent-hive cursor-pointer" />
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
